import React, { useContext } from "react";
import "./World.css";
import CSS from "csstype";

import { MainContext, MapHighlightMode, RenderMode } from "../App";
import { groupByPos, WorldHandler } from "../controllers/world";
import { ResourceNodeState, ResourceType } from "../controllers/resources";
import { CharacterState, StatusType } from "../controllers/characters";
import { Radio } from "antd";
import { UserAddOutlined, FrownOutlined } from "@ant-design/icons";

import { green, magenta, cyan } from "@ant-design/colors";
import WorldStats from "./Worldstats";

export const statusIcons: { [key: string]: JSX.Element } = {
  [`${StatusType.STARVING}`]: <FrownOutlined />,
  [`${StatusType.PREGNANT}`]: <UserAddOutlined />,
};

const getMapResourceViewStyles = (
  node: ResourceNodeState,
  resourceMax: number,
  color: string[]
): CSS.Properties => {
  const colorIndex =
    color.length -
    Math.floor(
      green.length * (node.quantity / (resourceMax + 100 * Number.EPSILON)) //only Number.EPSILON make runtime approximation to 1
    );

  return {
    color: colorIndex < 5 ? color[color.length - 1] : "white",
    backgroundColor: color[colorIndex],
  };
};

const getMapPopViewStyles = (
  characters: CharacterState[],
  maxCharacters: number,
  color: string[]
): CSS.Properties => {
  const colorIndex =
    color.length -
    Math.floor(
      magenta.length *
        (characters.length / (maxCharacters + 100 * Number.EPSILON)) //only Number.EPSILON make runtime approximation to 1
    );

  return {
    color: colorIndex < 5 ? color[color.length - 1] : "white",
    backgroundColor: color[colorIndex],
  };
};

const WorldComp: React.FC = () => {
  const { appState: context, setAppState: setContext } =
    useContext(MainContext);

  let worldGrid = [];
  const world = context.world;

  const maxCharacters = Object.entries(
    world.characters.reduce(groupByPos<CharacterState>(), {})
  ).reduce((maxPop: number, e) => Math.max(maxPop, e[1].length), 0);

  const gridSquareSize: number = world.size < 6 ? 60 : 40;

  for (let i = 0; i < world.size * world.size; ++i) {
    const posX = i % world.size;
    const posY = Math.floor(i / world.size);

    const characters = WorldHandler.getAllCharactersAtPos(
      { x: posX, y: posY },
      world
    );
    const resourceNodes = WorldHandler.getAllResourceNodesAtPos(
      { x: posX, y: posY },
      world
    );

    const popDisplay =
      characters.length > 0
        ? `P: ${
            characters.filter((c) =>
              c.statusList.find((s) => s.type === StatusType.ALIVE)
            ).length
          }`
        : "";

    const food = resourceNodes.find((r) => r.resource === ResourceType.FOOD);
    const foodDisplay = food ? `F: ${food.quantity}` : "";

    const preys = resourceNodes.find(
      (r) => r.resource === ResourceType.ANIMAL_PREY
    );

    const onSquareClick = () => {
      if (
        context.view.squareHighlighted == null ||
        context.view.squareHighlighted.x !== posX ||
        context.view.squareHighlighted.y !== posX
      ) {
        setContext({
          ...context,
          view: {
            ...context.view,
            squareHighlighted: { x: posX, y: posY },
          },
        });
      }
    };

    const mapViewStyles: CSS.Properties = (() => {
      switch (context.view.mapHighlightMode) {
        case MapHighlightMode.FOOD:
          const maxFood = world.resources
            .filter((r) => r.resource === ResourceType.FOOD)
            .reduce(
              (maxFood, foodNode) => Math.max(maxFood, foodNode.quantity),
              0
            );
          return food ? getMapResourceViewStyles(food, maxFood, green) : {};
        case MapHighlightMode.POP:
          return getMapPopViewStyles(characters, maxCharacters, magenta);
        case MapHighlightMode.ANIMAL_PREY:
          const maxPreys = world.resources
            .filter((r) => r.resource === ResourceType.ANIMAL_PREY)
            .reduce(
              (maxFood, foodNode) => Math.max(maxFood, foodNode.quantity),
              0
            );
          return preys ? getMapResourceViewStyles(preys, maxPreys, cyan) : {};
        default:
          throw new Error(
            `Unknown MapHighlightMode: ${context.view.mapHighlightMode}`
          );
      }
    })();

    const charHighlighted = context.view.characterHighlighted
      ? WorldHandler.getCharacter(
          context.world,
          context.view.characterHighlighted
        )
      : null;
    const characterHighlightedClassname =
      context.view.characterHighlighted &&
      charHighlighted &&
      charHighlighted.pos.x === posX &&
      charHighlighted.pos.y === posY
        ? "highlighted"
        : "";
    worldGrid[i] = (
      <div
        className={`grid-square ${characterHighlightedClassname}`}
        style={{
          ...mapViewStyles,
          width: `${gridSquareSize}px`,
          height: `${gridSquareSize}px`,
        }}
        key={`${posX}x${posY}`}
        onClick={onSquareClick}
      >
        <div>{popDisplay}</div>
        <div>{foodDisplay}</div>
      </div>
    );
  }

  const gridStyle = {
    width: `${world.size * gridSquareSize}px`,
    margin: "auto",
    gridTemplateColumns: `repeat(${world.size}, 1fr)`,
    gridTemplateRows: `repeat(${world.size}, 1fr)`,
  };

  return (
    <div style={{ textAlign: "center", marginBottom: "20px" }}>
      <WorldStats />
      <div className="worldGrid" style={gridStyle}>
        {context.view.renderModes.find((rm) => RenderMode.REACT_GRID)
          ? worldGrid
          : ""}
      </div>
      <Radio.Group
        value={context.view.mapHighlightMode}
        onChange={(e) =>
          setContext({
            ...context,
            view: {
              ...context.view,
              mapHighlightMode: e.target.value,
            },
          })
        }
      >
        <Radio.Button value={MapHighlightMode.POP}>POP</Radio.Button>
        <Radio.Button value={MapHighlightMode.FOOD}>Food</Radio.Button>
        <Radio.Button value={MapHighlightMode.ANIMAL_PREY}>Prey</Radio.Button>
      </Radio.Group>
    </div>
  );
};

export default WorldComp;
